/**
 * QuantityControl - Quantity input control with min/max validation
 * Respects qtyRequired floor for required items
 */

import React from 'react';
import { Plus, Minus } from 'lucide-react';

const QuantityControl = ({ 
  quantity, 
  minQuantity = 0, 
  maxQuantity = 99, 
  onChange, 
  disabled = false,
  size = 'sm'
}) => {
  const canDecrease = quantity > minQuantity && !disabled;
  const canIncrease = quantity < maxQuantity && !disabled;

  const handleDecrease = () => {
    if (canDecrease) {
      onChange(quantity - 1);
    }
  };

  const handleIncrease = () => {
    if (canIncrease) {
      onChange(quantity + 1);
    }
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= minQuantity && value <= maxQuantity) {
      onChange(value);
    }
  };

  const buttonClass = size === 'sm' 
    ? 'h-6 w-6 text-xs' 
    : 'h-8 w-8 text-sm';
  
  const inputClass = size === 'sm'
    ? 'h-6 w-12 text-xs'
    : 'h-8 w-16 text-sm';

  return (
    <div className="flex items-center space-x-1">
      <button
        type="button"
        onClick={handleDecrease}
        disabled={!canDecrease}
        className={`${buttonClass} flex items-center justify-center rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white`}
        title={quantity <= minQuantity ? `Minimum quantity: ${minQuantity}` : 'Decrease quantity'}
      >
        <Minus className="h-3 w-3" />
      </button>
      
      <input
        type="number"
        value={quantity}
        onChange={handleInputChange}
        min={minQuantity}
        max={maxQuantity}
        disabled={disabled}
        className={`${inputClass} text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
      />
      
      <button
        type="button"
        onClick={handleIncrease}
        disabled={!canIncrease}
        className={`${buttonClass} flex items-center justify-center rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white`}
        title={quantity >= maxQuantity ? `Maximum quantity: ${maxQuantity}` : 'Increase quantity'}
      >
        <Plus className="h-3 w-3" />
      </button>
      
      {minQuantity > 0 && (
        <span className="text-xs text-gray-500 ml-1">
          (min: {minQuantity})
        </span>
      )}
    </div>
  );
};

export default QuantityControl;